import { supabase } from '@/lib/supabase';
import {
  SeoKeywordOpportunity,
  SeoOpportunityAction,
  SeoOpportunitySnapshot,
  FixGuidance,
  AiSuggestion,
  OpportunityStatus,
} from '@/types/api/seo';

function mapOpportunityRow(row: any): SeoKeywordOpportunity {
  return {
    id: row.id,
    projectId: row.project_id,
    keywordId: row.keyword_id,
    opportunityType: row.opportunity_type,
    priority: row.priority,
    title: row.title,
    description: row.description,
    metric: row.metric,
    impactScore: row.impact_score ?? 50,
    aiFixGuidance: row.ai_fix_guidance,
    aiSuggestions: row.ai_suggestions,
    detectedPosition: row.detected_position,
    detectedCtr: row.detected_ctr,
    detectedClicks: row.detected_clicks,
    detectedImpressions: row.detected_impressions,
    status: row.status,
    isAiGenerated: row.is_ai_generated,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    resolvedAt: row.resolved_at,
    expiresAt: row.expires_at,
  };
}

function mapActionRow(row: any): SeoOpportunityAction {
  return {
    id: row.id,
    opportunityId: row.opportunity_id,
    actionType: row.action_type,
    description: row.description,
    notes: row.notes,
    positionAtAction: row.position_at_action,
    ctrAtAction: row.ctr_at_action,
    clicksAtAction: row.clicks_at_action,
    impressionsAtAction: row.impressions_at_action,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

function mapSnapshotRow(row: any): SeoOpportunitySnapshot {
  return {
    id: row.id,
    opportunityId: row.opportunity_id,
    position: row.position,
    ctr: row.ctr,
    clicks: row.clicks,
    impressions: row.impressions,
    snapshotDate: row.snapshot_date,
  };
}

const seoOpportunitiesService = {
  async getByKeyword(keywordId: string): Promise<SeoKeywordOpportunity[]> {
    const { data, error } = await supabase
      .from('seo_keyword_opportunities')
      .select('*')
      .eq('keyword_id', keywordId)
      .neq('status', 'dismissed')
      .gt('expires_at', new Date().toISOString())
      .order('priority', { ascending: true })
      .order('impact_score', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapOpportunityRow);
  },

  async upsert(params: {
    projectId: string;
    keywordId: string;
    opportunityType: string;
    priority: 'high' | 'medium' | 'low';
    title: string;
    description: string;
    metric: string;
    impactScore?: number;
    aiFixGuidance?: FixGuidance | null;
    detectedPosition?: number | null;
    detectedCtr?: number | null;
    detectedClicks?: number | null;
    detectedImpressions?: number | null;
    isAiGenerated?: boolean;
  }): Promise<SeoKeywordOpportunity> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('seo_keyword_opportunities')
      .upsert({
        project_id: params.projectId,
        keyword_id: params.keywordId,
        opportunity_type: params.opportunityType,
        priority: params.priority,
        title: params.title,
        description: params.description,
        metric: params.metric,
        impact_score: params.impactScore ?? 50,
        ai_fix_guidance: params.aiFixGuidance ?? null,
        detected_position: params.detectedPosition ?? null,
        detected_ctr: params.detectedCtr ?? null,
        detected_clicks: params.detectedClicks ?? null,
        detected_impressions: params.detectedImpressions ?? null,
        is_ai_generated: params.isAiGenerated ?? false,
        created_by: user.id,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'keyword_id,opportunity_type' })
      .select()
      .single();

    if (error) throw error;
    return mapOpportunityRow(data);
  },

  async bulkUpsert(
    projectId: string,
    keywordId: string,
    opportunities: Array<{
      opportunityType: string;
      priority: 'high' | 'medium' | 'low';
      title: string;
      description: string;
      metric: string;
      impactScore?: number;
      aiFixGuidance?: FixGuidance | null;
      detectedPosition?: number | null;
      detectedCtr?: number | null;
      detectedClicks?: number | null;
      detectedImpressions?: number | null;
      isAiGenerated?: boolean;
    }>
  ): Promise<SeoKeywordOpportunity[]> {
    const results: SeoKeywordOpportunity[] = [];
    for (const opp of opportunities) {
      try {
        const result = await this.upsert({ projectId, keywordId, ...opp });
        results.push(result);
      } catch {
        // best-effort per item
      }
    }
    return results;
  },

  async updateStatus(id: string, status: OpportunityStatus): Promise<void> {
    const updates: Record<string, any> = {
      status,
      updated_at: new Date().toISOString(),
    };
    if (status === 'completed') {
      updates.resolved_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('seo_keyword_opportunities')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  },

  async saveFixGuidance(id: string, guidance: FixGuidance): Promise<void> {
    const { error } = await supabase
      .from('seo_keyword_opportunities')
      .update({ ai_fix_guidance: guidance, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async saveSuggestions(id: string, suggestions: AiSuggestion[]): Promise<void> {
    const { error } = await supabase
      .from('seo_keyword_opportunities')
      .update({ ai_suggestions: suggestions, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async addAction(params: {
    opportunityId: string;
    actionType: string;
    description: string;
    notes?: string;
    positionAtAction?: number | null;
    ctrAtAction?: number | null;
    clicksAtAction?: number | null;
    impressionsAtAction?: number | null;
  }): Promise<SeoOpportunityAction> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('seo_opportunity_actions')
      .insert({
        opportunity_id: params.opportunityId,
        action_type: params.actionType,
        description: params.description,
        notes: params.notes ?? null,
        position_at_action: params.positionAtAction ?? null,
        ctr_at_action: params.ctrAtAction ?? null,
        clicks_at_action: params.clicksAtAction ?? null,
        impressions_at_action: params.impressionsAtAction ?? null,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return mapActionRow(data);
  },

  async getActions(opportunityId: string): Promise<SeoOpportunityAction[]> {
    const { data, error } = await supabase
      .from('seo_opportunity_actions')
      .select('*')
      .eq('opportunity_id', opportunityId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapActionRow);
  },

  async getSnapshots(opportunityId: string): Promise<SeoOpportunitySnapshot[]> {
    const { data, error } = await supabase
      .from('seo_opportunity_snapshots')
      .select('*')
      .eq('opportunity_id', opportunityId)
      .order('snapshot_date', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapSnapshotRow);
  },

  async upsertTodaySnapshot(
    opportunityId: string,
    metrics: { position: number | null; ctr: number | null; clicks: number | null; impressions: number | null }
  ): Promise<void> {
    const today = new Date().toISOString().split('T')[0];

    const { error } = await supabase
      .from('seo_opportunity_snapshots')
      .upsert({
        opportunity_id: opportunityId,
        position: metrics.position,
        ctr: metrics.ctr,
        clicks: metrics.clicks,
        impressions: metrics.impressions,
        snapshot_date: today,
      }, { onConflict: 'opportunity_id,snapshot_date' });

    if (error) { /* best-effort */ }
  },
};

export default seoOpportunitiesService;
